/**
 * 混淆模式，端内走桥接，端外走 http 上报
 */
import type { HybridWeblogInitParams, PageTypeKey, UrlObj } from '@/types/configuration';
import type {
    EventType as IEventType,
    EventOptions as IEventOptions,
    BizEventTypes,
    CustomEventTypes,
    TaskActions
} from '@/types/event';
import BaseLogger from '../browser';
import { logFactory } from './log';
import { checkSupportCloseCoPage, pageUrlValidate, stringifyParams } from '@/util';
import { log } from '@/util/console';
import { isInYoda, isSupportBridgeLog, setClientLog } from '@/util/yodav2Browser';
import type { SetClientLogTypes } from '@/util/yodav2';
import UrlPackage from '../browser/url-package';
import { isKDSNative } from '@/util/ua';
import { invoke, version } from '@yoda/bridge';
import ssrGlobal from '@/util/ssr-global';

/**
 * 判断当前yoda的状态
 * INIT: 初始状态
 * CHECKING：检查是否可用中，此时上报的埋点都会推入等待队列
 * READY：可用，此时上报的埋点会以桥接方式上报
 * DISABLED：不可用，此时上报的埋点会以 http 方式上报
 */

enum YodaStatus {
    INIT = -1,
    CHECKING = 0,
    READY = 1,
    DISABLED = 2,
}
type SendParams<T extends IEventType> = {
    type: T,
    options: IEventOptions<T>,
    immediately?: boolean,
};

const getMockYoda = () => {
    try {
        if (isInYoda()) {
            return {
                invoke,
                version
            };
        }
    } catch (err) {}
    console.warn('Not in yoda webview!');
    return;
};

export default class BridgeWeblogger extends BaseLogger {
    yodaStatus: YodaStatus = YodaStatus.INIT;
    waitYodaQueue: SendParams<BizEventTypes | TaskActions | CustomEventTypes>[];
    // 记录页面是否已发送过 enter pv;
    entered: String = '';
    constructor(
        logConfig: HybridWeblogInitParams[0],
        base?: HybridWeblogInitParams[1],
    ) {
        super({ ...logConfig, yoda: getMockYoda(), bridgeMode: !logConfig?.disableBridge }, base);
        if (logConfig?.disableBridge || !isInYoda()) {
            this.yodaStatus = YodaStatus.DISABLED;
        } else {
            this.checkSupportBridge();
        }
        // @ts-ignore
        if (!this.waitYodaQueue) {
            this.waitYodaQueue = [];
        }
    }
    private async checkSupportBridge() {
        const isSupport = await isSupportBridgeLog();
        this.yodaStatus = isSupport ? YodaStatus.READY : YodaStatus.DISABLED;
        this.waitYodaQueue.forEach(item => {
            const { type, options, immediately } = item;
            if (isSupport) {
                this.sendByBridge(type, options, immediately);
            } else {
                this.sendByHttp(type, options, immediately);
            }
        });
        this.waitYodaQueue = [];
    }
    /**
     * 桥接上报
     */
    sendByBridge<T extends IEventType>(
        eventType: T,
        eventOptions: IEventOptions<T>,
        immediately?: boolean,
    ) {
        const { disablePV: configDisablePV, biz } = this.logConfig;
        let { contentPackage, disablePV: eventDisablePV, bridgeFields = {}, urlPage } = eventOptions as IEventOptions<'CLICK'>;
        const disablePV = configDisablePV || eventDisablePV;
        if (contentPackage) {
            contentPackage = typeof contentPackage === 'string' ? contentPackage : JSON.stringify(contentPackage);
        }
        let setClientLogType: SetClientLogTypes = 'addTaskEvent';
        const isPVEvent = eventType === 'PV';
        if (isPVEvent) {
            if (disablePV) return;
            // bridge PV 仅支持 enter 和 visible
            const supportPVTypes = ['enter', 'visible'];
            let { page, type, params, coPage, category } = eventOptions as IEventOptions<'PV'>;
            if (!type) (eventOptions as IEventOptions<'PV'>).type = type = 'enter';
            if (type === 'leave' && this.currentUrlPackage.coPage && checkSupportCloseCoPage()) {
                setClientLog('closeCoPage', {
                    ...eventOptions,
                    ...this.currentUrlPackage,
                });
                return;
            }
            if (supportPVTypes.indexOf(type) === -1) {
                return;
            }
            // enter 则强行重新生成新的 currentUrlPackage，否则局部更新
            if (type === 'enter') {
                let realPage = page || this.currentUrlPackage.page;
                this.updateCurrentUrlPackage({ page: realPage, params: !page ? this.currentUrlPackage.params : params, force: true });
                if (coPage) {
                    this.currentUrlPackage.coPage = coPage;
                }
                if (category) {
                    this.currentUrlPackage.category = category;
                }
                this.entered = realPage;
            } else if (page === this.entered) {
                this.currentUrlPackage.update(page, params);
            }
            // 对于上报到v2，page为url的埋点，给予提示
            if (!pageUrlValidate(this.currentUrlPackage.page)) return;
            setClientLogType = 'setCurrentPage';
        }
        const coPage = !!this.currentUrlPackage.coPage;
        const urlPackage = urlPage?.page && !isPVEvent ? {
            page: urlPage.page,
            identity: urlPage.identity,
            params: stringifyParams(urlPage.params),
            page_type: this.currentUrlPackage.page_type
        } : {
            ...this.currentUrlPackage.toJSON(),
            coPage
        }
        const log = logFactory(eventType, {
            biz,
            ...eventOptions,
            contentPackage,
            currentUrlPackage: disablePV ? undefined : urlPackage,
        } as any); // TODO: 待优化 logFactory 参数
        const isCustomType = ['CUSTOM', 'RADAR', 'CUSTOM_EVENT'].indexOf(eventType) !== -1;
        // 保持 h5ExtraAttr 来源统一
        this.commonPackage.increaseH5SeqId(isCustomType);
        // 设置 log 公共属性
        const { service_name, sub_biz, need_encrypt } = this.baseOption || {};
        // TODO: 桥接上报不需要 common_package 信息，可以优化
        log.h5ExtraAttr = JSON.stringify(this.commonPackage.getH5ExtraAttr({ bridge_info: this.logConfig?.yoda?.version || version || true, coPage }));
        log.realtime = !!immediately;
        log.serviceName = service_name || '';
        log.subBiz = sub_biz || '';
        log.needEncrypt = need_encrypt || false;
        log.container = isKDSNative() ? 'REACT_NATIVE' : 'H5';
        if (eventType === 'SHOW') {
            setClientLogType = 'addElementShowEvent';
        } else if (isCustomType) {
            // 兼容业务一些迁移行为，自动把 custom_stat_event 转为 custom_event
            setClientLogType = (eventType === 'CUSTOM_EVENT' || this.logConfig.customStatToCustom) ? 'addCustomEvent' : 'addCustomStatEvent';
        }
        setClientLog(setClientLogType, {
            ...bridgeFields,
            ...log,
        });
    }

    override updateCurrentUrlPackage(url: string | UrlObj = ssrGlobal.location?.href || '', type: PageTypeKey = 'web') {
        if (typeof url === 'object' && !url.force && this.currentUrlPackage) {
            const { page = this.currentUrlPackage.page, params = {} } = url;
            if (!page || page === this.currentUrlPackage.page) {
                // 兼容降级为 http 上报情况
                this.currentUrlPackage.update(page, params);
                const isPageEntered =  this.entered === page;
                if (this.yodaStatus !== YodaStatus.DISABLED && isPageEntered) {
                    // 客户端内直接使用 updateCurrentUrlPackage 更改页面参数是无法成功的
                    // 客户端只认 pv 上报时候带上去的参数，因此此处发送一个visible类型的pv，改变客户端维护的页面参数
                    this.send('PV', {
                        type: 'visible',
                        page,
                        params,
                    }, true);
                }
                return;
            }
        }
        this.currentUrlPackage = new UrlPackage(url, type, this.logConfig.urlMap);
        if (this.logConfig.attachUrl) this.currentUrlPackage.attachUrl();
    }
    /**
     * Http 上报
     * @param type
     * @param name
     * @param params
     * @param contentPackage
     * @param immediately
     */
    sendByHttp<T extends IEventType>(
        type: T,
        options: IEventOptions<T>,
        immediately?: boolean,
    ) {
        return super.send(type, options, immediately);
    }
    /**
     * 复写父类 send 方法
     * @param type
     * @param name
     * @param params
     * @param contentPackage
     * @param immediately
     */
    override send<T extends IEventType>(
        type: T,
        options: IEventOptions<T>,
        immediately?: boolean,
    ) {
        const { enableV3CustomEvent, openHttpSender } = this.logConfig;
        // 目前只有enableV3CustomEvent打开时强制自定义事件上报到v3，注意此时应限制用户设置proto为v2
        const CustomToV3 = type === 'CUSTOM' && enableV3CustomEvent;

        /**
         * 强制 http 上报或yoda不可用，降级使用 http 上报
         *
         * 其中的 disabled 逻辑与 isInYoda 逻辑判断并不冲突。
         * 增加 isInYoda 判断是为了兼容 autopv 发送，autopv 发送时父BaseLogger类会调用子(也就是本类）的send 方法，
         * 这个时候 yoda 状态，waitYodaQueue 等参数还未初始化)
         *
         * 逻辑说明：
         * 如果是端外情况（不在 yoda 环境），可不用判断，直接使用 http 发送即可。
         * 如果是端内情况（拥有yoda 环境）这个 if 条件不会进入，会走下面将埋点存入 waitYodaQueue 列表逻辑，判断 yoda 使用状态后，再决定如何发送。
          */
        if (CustomToV3 || this.yodaStatus === YodaStatus.DISABLED || !isInYoda()) {
            return this.sendByHttp(type, options, immediately);
        }
        // 双报模式
        if (openHttpSender) {
            this.sendByHttp(type, options, immediately);
        }
        if (this.yodaStatus === YodaStatus.INIT) {
            this.yodaStatus = YodaStatus.CHECKING;
        }
        // yodaStatus 为 undefined 是 autopv 插件最开始发送，hybrid 内部还没有来得及初始化的情况
        if (this.yodaStatus === YodaStatus.CHECKING || typeof this.yodaStatus === 'undefined') {
            log('[weblogger]: waiting yoda ready');
            if (!this.waitYodaQueue) {
                this.waitYodaQueue = [];
            }
            this.waitYodaQueue.push({
                type,
                options,
                immediately,
            });
            return;
        }
        return this.sendByBridge(type, options, immediately);
    }
}
